import React, { useCallback, useState, useEffect } from "react";
import {
  ButtonContainer,
  CustomerActionButtons,
  CustomerActionsCell,
  CustomerCell,
  CustomerRow,
  CustomersList,
  CustomerSettingsTitle,
  ListMessage,
  TitleRow,
} from "./customerSettings.styled";
import { LabeledValue } from "components/designV2/labeledValue/labeledValue";
import { useTranslation } from "react-i18next";
import { PrimaryButton } from "components/designV2/buttons/primaryButton";
import { SecondaryButton } from "components/designV2/buttons/secondaryButton";
import { AddCustomerForm } from "./addCustomerForm.component";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import { CompanyCustomer } from "model/Company";
import { CustomerModal } from "./customerModal.component";
import { CustomerInvoiceModal } from "./customerInvoiceModal.component";
import { useAlertStore } from "stores/alertStore/alertStore";
import { useCustomers } from "./useCustomers.hook";

export const CustomerSettings: React.FC = () => {
  const { t } = useTranslation();
  const [showAddCustomerForm, setShowAddCustomerForm] = useState(false);
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [showCustomerInvoiceModal, setShowCustomerInvoiceModal] =
    useState(false);

  const [companyState] = useCompanyStore();
  const [, , alertDispatch] = useAlertStore();
  const [selectedCustomer, setSelectedCustomer] =
    useState<CompanyCustomer | null>(null);
  const {
    customers,
    fetchCustomers,
    isSavingCustomer,
    replaceCustomers,
    saveCustomer,
  } = useCustomers();

  const showCustomerError = useCallback((): void => {
    alertDispatch({
      type: "SHOW_ALERT",
      payload: {
        icon: "alert",
        title: t("Alerts.OhNo"),
        message: t("General.ErrorOccurred"),
      },
    });
  }, [alertDispatch, t]);

  const onSaveCustomer = async (
    customer: CompanyCustomer,
  ): Promise<boolean> => {
    const wasSaved = await saveCustomer(customer);

    if (!wasSaved) {
      showCustomerError();
    }

    return wasSaved;
  };

  const companyId = companyState.company?.id;

  useEffect(() => {
    void fetchCustomers(companyId).then((wasFetched) => {
      if (!wasFetched) {
        showCustomerError();
      }
    });
  }, [companyId, fetchCustomers, showCustomerError]);

  return (
    <>
      <TitleRow>
        <CustomerSettingsTitle>
          {t("CompanyCustomers.Customers")}
        </CustomerSettingsTitle>
        <ButtonContainer>
          <PrimaryButton
            label={t("CompanyCustomers.AddCustomer")}
            iconName="v2/plus"
            onClick={() => setShowAddCustomerForm(true)}
          />
        </ButtonContainer>
      </TitleRow>

      <CustomersList>
        {customers.length === 0 ? (
          <ListMessage>{t("General.None")}</ListMessage>
        ) : (
          customers.map((customer: CompanyCustomer) => (
            <CustomerRow key={customer.organizationNumber}>
              <CustomerCell>
                <LabeledValue
                  label={t("CompanyCustomers.Customer")}
                  value={customer.name}
                  variant="small"
                  truncate
                />
              </CustomerCell>

              <CustomerCell>
                <LabeledValue
                  label={t("CompanyCustomers.OrganizationNumber")}
                  value={customer.organizationNumber}
                  variant="small"
                  truncate
                />
              </CustomerCell>

              <CustomerCell>
                <LabeledValue
                  label={t("CompanyCustomers.Contact")}
                  value={customer.contact}
                  variant="small"
                  truncate
                />
              </CustomerCell>

              <CustomerActionsCell>
                <CustomerActionButtons>
                  <SecondaryButton
                    label={t("CompanyCustomers.CreateInvoice")}
                    iconName="v2/plus"
                    size="small"
                    onClick={() => {
                      setSelectedCustomer(customer);
                      setShowCustomerInvoiceModal(true);
                    }}
                  />

                  <SecondaryButton
                    label={t("CompanyCustomers.AddProduct")}
                    iconName="v2/plus"
                    size="small"
                    onClick={() => {
                      setSelectedCustomer(customer);
                      setShowCustomerModal(true);
                    }}
                  />
                </CustomerActionButtons>
              </CustomerActionsCell>
            </CustomerRow>
          ))
        )}
      </CustomersList>

      <AddCustomerForm
        isSaving={isSavingCustomer}
        setShowAddCustomerForm={setShowAddCustomerForm}
        showAddCustomerForm={showAddCustomerForm}
        saveCustomer={onSaveCustomer}
      />

      <CustomerModal
        setShowCustomerModal={setShowCustomerModal}
        showCustomerModal={showCustomerModal}
        customer={selectedCustomer}
        setCustomers={replaceCustomers}
      />
      <CustomerInvoiceModal
        setShowCustomerInvoiceModal={setShowCustomerInvoiceModal}
        showCustomerInvoiceModal={showCustomerInvoiceModal}
        customer={selectedCustomer}
        companyId={companyState.company?.id}
      />
    </>
  );
};
